'use client';

import { cn } from '@/lib/utils';
import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode, type RefObject } from 'react';

type DropdownOverlayPanelProps = ComponentPropsWithoutRef<'div'> & {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    ignoreRefs?: Array<RefObject<HTMLElement | null>>;
};

export const DropdownOverlayPanel = ({
    open,
    onClose,
    className,
    children,
    ignoreRefs = [],
    ...props
}: DropdownOverlayPanelProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    const ignoreRefsRef = useRef(ignoreRefs);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        ignoreRefsRef.current = ignoreRefs;
    }, [ignoreRefs]);

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;

            if (panelRef.current?.contains(target)) {
                return;
            }

            if (ignoreRefsRef.current.some((ref) => ref.current?.contains(target))) {
                return;
            }

            onCloseRef.current();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    if (!open) return null;

    return (
        <div ref={panelRef} className={cn('w-full', className)} {...props}>
            {children}
        </div>
    );
};
